import { useMemo, useState } from 'react';
import { formatFileSize } from '@brimo/shared';
import { Banner } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import { completeTask, sendMessage, useContactDocuments, useMailConnection } from '../api';
import { berlinInstant, formatDayKey } from '../format';
import type { CreateMessageBody } from '../types';
import { EditorCard, SplitSendButton } from './EditorCard';
import { CheckList, EditorField, FollowUpField, validTime } from './fields';
import { followUpOf, isNextStepRequired, isValidation, useNextStepGate, type EditorProps } from './shared';
import styles from './editor.module.css';

interface Errors {
  subject?: string | undefined;
  text?: string | undefined;
  followUp?: string | undefined;
}

/** Kontaktakte II.E2 „E-Mail" (with III.T1 „Später senden"): subject, text, attachments from the record's files, follow-up. */
export function EmailEditor({ record, task, onClose, onDone }: EditorProps) {
  const session = useSession();
  const mailbox = useMailConnection(true);
  const documents = useContactDocuments(record.id, true);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<ReadonlySet<string>>(new Set());
  const [followUp, setFollowUp] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const files = useMemo(
    () =>
      (documents.data?.items ?? []).filter(
        (row) => row.kind === 'document' && row.fileId !== null && row.canOpen,
      ),
    [documents.data],
  );
  const to = record.header.email;
  const subtitle = mailbox.data?.identifier
    ? `Versand über ${mailbox.data.identifier}`
    : `Versand über die Systemadresse · Antworten gehen an ${session.account.email}`;
  const taskTitle = `Follow-up zu: ${subject.trim() || text.trim().split('\n')[0] || `E-Mail an ${record.header.name}`}`;

  function buildBody(sendAt: string | undefined): CreateMessageBody | null {
    const next: Errors = {};
    if (subject.trim() === '') next.subject = 'Bitte einen Betreff angeben.';
    if (text.trim() === '') next.text = 'Bitte einen Text angeben.';
    if (followUp && !validTime(time)) next.followUp = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return null;
    const body: CreateMessageBody = {
      contactId: record.id,
      channel: 'email',
      subject: subject.trim(),
      text: text.trim(),
    };
    if (attachments.size > 0) body.attachmentFileIds = [...attachments];
    if (sendAt) body.sendAt = sendAt;
    const due = followUpOf(followUp, date, time);
    if (due) body.followUp = due;
    return body;
  }

  const gate = useNextStepGate(record.header.name, async (step) => {
    if (!task) return;
    const done = await completeTask(task.id, { outcomeType: 'email_sent', nextStep: step });
    onDone({
      toast: 'E-Mail gesendet.',
      next:
        done.next === 'book_appointment'
          ? { editor: 'appointment', completesTaskId: done.createdTaskId }
          : undefined,
    });
  });

  async function send(sendAt: string | undefined, toast: string) {
    const body = buildBody(sendAt);
    if (!body) return;
    setPending(true);
    setBanner(null);
    try {
      await sendMessage(body);
    } catch (error) {
      if (isValidation(error)) {
        const fields = error.fields;
        const mapped: Errors = {
          subject: fields['subject'],
          text: fields['text'],
          followUp: fields['followUp.dueOn'] ?? fields['followUp.dueTime'],
        };
        setErrors(mapped);
        const other = fields['to'] ?? fields['attachmentFileIds'];
        if (other || !(mapped.subject || mapped.text || mapped.followUp)) setBanner(other ?? error.message);
      } else setBanner(errorMessage(error));
      setPending(false);
      return;
    }
    if (task) {
      try {
        const done = await completeTask(task.id, { outcomeType: 'email_sent' });
        onDone({
          toast,
          next:
            done.next === 'book_appointment'
              ? { editor: 'appointment', completesTaskId: done.createdTaskId }
              : undefined,
        });
      } catch (error) {
        setPending(false);
        if (isNextStepRequired(error)) gate.open();
        else setBanner(errorMessage(error));
      }
      return;
    }
    onDone({ toast });
  }

  return (
    <>
      <EditorCard
        tone="paper"
        dot="var(--info-fg)"
        title={to ? `E-Mail an ${to}` : `E-Mail an ${record.header.name}`}
        subtitle={subtitle}
        footnote="Geht in Ihrem Namen raus."
        label="E-Mail-Editor"
        onClose={onClose}
        primary={
          <SplitSendButton
            loading={pending}
            disabled={to === null}
            onSend={() => void send(undefined, 'E-Mail gesendet.')}
            onSendLater={(day, at) =>
              void send(
                berlinInstant(day, at || '09:00'),
                `E-Mail geplant für ${formatDayKey(day)}, ${at || '09:00'}.`,
              )
            }
          />
        }
      >
        {to === null ? <Banner tone="warning">Der Kontakt hat keine E-Mail-Adresse.</Banner> : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <EditorField label="Betreff" error={errors.subject}>
          <input
            className={styles.input}
            aria-label="Betreff"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            autoFocus
          />
        </EditorField>
        <EditorField label="Nachricht" error={errors.text}>
          <textarea
            className={styles.textarea}
            rows={5}
            aria-label="Nachricht"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </EditorField>
        {files.length > 0 ? (
          <EditorField
            label="Anhänge"
            hint={`${files.length} ${files.length === 1 ? 'Datei' : 'Dateien'} in der Akte`}
          >
            <CheckList
              items={files.map((row) => ({
                id: row.id,
                label: row.fileName ?? row.name,
                meta: [row.origin, row.partyLabel, row.size !== null ? formatFileSize(row.size) : null]
                  .filter(Boolean)
                  .join(' · '),
                checked: row.fileId !== null && attachments.has(row.fileId),
                onChange: (checked) =>
                  setAttachments((current) => {
                    const next = new Set(current);
                    if (row.fileId === null) return next;
                    if (checked) next.add(row.fileId);
                    else next.delete(row.fileId);
                    return next;
                  }),
              }))}
            />
          </EditorField>
        ) : null}
        <FollowUpField
          checked={followUp}
          onCheckedChange={setFollowUp}
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          taskTitle={taskTitle}
          error={errors.followUp}
        />
      </EditorCard>
      {gate.dialog}
    </>
  );
}
